import { Model, type ModelProps } from '@/infra/model'
import { action, computed, observable } from 'mobx'
import type { SupportingItemInputBase } from '../supporting-item-input'
import type { Id } from '@/infra/id'
import { toStream } from '@/utils/mobx-util'

/**
 * Props for {@link DependentsInfoSupportingItemInput}.
 */
export type DependentsInfoSupportingItemInputProps =
  ModelProps<DependentsInfoSupportingItemInput> & { type: 'DependentsInfo' }

/**
 * Supporting item input for dependents' information.
 */
export class DependentsInfoSupportingItemInput
  extends Model<DependentsInfoSupportingItemInput>
  implements SupportingItemInputBase
{
  @observable.struct accessor dependents: readonly ProvidedDependent[] = []

  /**
   * Stream of changes to the provided dependents.
   */
  readonly providedChanges$ = toStream(() => this.dependents)

  /** @inheritDoc */
  @computed get isCompleted() {
    return this.dependents.length > 0
  }

  /**
   * Adds a dependent to the input.
   */
  @action.bound
  addDependent(dependent: ProvidedDependent) {
    this.dependents = [
      ...this.dependents.filter((d) => d.profileId !== dependent.profileId),
      dependent,
    ]
  }

  /**
   * Removes a dependent from the input.
   */
  @action.bound
  removeDependent(profileId: Id) {
    this.dependents = this.dependents.filter((d) => d.profileId !== profileId)
  }
}

/**
 * A dependent provided to a {@link DependentsInfoSupportingItemInput}.
 */
export interface ProvidedDependent {
  readonly profileId: Id
  readonly profileVersion: number
}
